<script setup lang="ts">
import { plusMonths, systemClock, todayUtc } from '@taler/domain'
import { Card, ErrorState, PageShell, Select } from '@taler/ui'
import DetailNotFound from '@/app/components/DetailNotFound.vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute } from 'vue-router'
import {
  canViewForecast,
  ForecastEventForm,
  ForecastEventList,
  ForecastProjectionChart,
  ForecastTargetCheck,
  ForecastVariableForm,
  ForecastVariableList,
  useForecastScenario,
} from '@/features/forecast'
import { useTargets } from '@/features/targets'
import { useDisplayCurrency } from '@/app/display-currency'
import { useSession } from '@/app/session'

const { t } = useI18n()
const route = useRoute()
const session = useSession()

const scenarioId = computed(() => route.params['scenarioId'] as string)
const canManage = computed(() => canViewForecast(session.can))

const HORIZONS = [3, 6, 12, 24] as const
const horizonMonths = ref<number>(12)
const horizonOptions = HORIZONS.map((value) => ({
  value,
  label: t('forecast.monthsAhead', { count: value }),
}))
const { displayCurrency } = useDisplayCurrency()
const targetId = ref<string | undefined>(undefined)

const from = computed(() => todayUtc(systemClock))
const through = computed(() => plusMonths(from.value, horizonMonths.value))

const scenario = useForecastScenario({
  ledgerId: () => session.activeLedger.value?.id,
  scenarioId: () => scenarioId.value,
  // The route already requires RECORDS_MANAGE (routes.ts); viewing is not gated a second
  // time here, the same way LoanDetail's own view query is unconditional.
  enabled: () => true,
  from: () => from.value,
  through: () => through.value,
  displayCurrency: () => displayCurrency.value,
  targetId: () => targetId.value,
})

const targets = useTargets({
  ledgerId: () => session.activeLedger.value?.id,
  enabled: () => canManage.value,
})

const isFlat = computed(() => scenario.variables.value.length === 0 && scenario.events.value.length === 0)

const variableErrorMessage = computed(() =>
  scenario.hasVariableError.value
    ? (scenario.variableErrorDetail.value ?? t('forecast.variableErrorBody'))
    : undefined,
)
const eventErrorMessage = computed(() =>
  scenario.hasEventError.value
    ? (scenario.eventErrorDetail.value ?? t('forecast.eventErrorBody'))
    : undefined,
)

function createVariable(input: Parameters<typeof scenario.createVariable>[0]): void {
  scenario.createVariable(input).catch(() => {
    // surfaced through scenario.hasVariableError; nothing further to do here
  })
}

function deleteVariable(variableId: string): void {
  scenario.deleteVariable(variableId).catch(() => {
    // surfaced through the variable staying in the list; nothing further to do here
  })
}

function createEvent(input: Parameters<typeof scenario.createEvent>[0]): void {
  scenario.createEvent(input).catch(() => {
    // surfaced through scenario.hasEventError; nothing further to do here
  })
}

function deleteEvent(eventId: string): void {
  scenario.deleteEvent(eventId).catch(() => {
    // surfaced through the event staying in the list; nothing further to do here
  })
}
</script>

<template>
  <PageShell class="gap-6">
    <p v-if="session.isLoading.value || scenario.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <DetailNotFound
      v-else-if="scenario.notFound.value"
      :to="{ name: 'forecast' }"
      :list="t('planning.title')"
    />

    <ErrorState
      v-else-if="scenario.error.value"
      :title="t('forecast.detailErrorTitle')"
      :body="t('forecast.errorBody')"
      :retry-label="t('forecast.retry')"
      :retrying="scenario.retrying.value"
      @retry="scenario.retry()"
    />

    <template v-else-if="scenario.scenario.value">
      <div class="flex flex-col gap-2">
        <RouterLink
          :to="{ name: 'forecast' }"
          class="w-fit text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
        >
          ← {{ t('forecast.title') }}
        </RouterLink>
        <h1 class="text-[length:var(--text-step-6)] font-semibold text-[color:var(--color-text)]">
          {{ scenario.scenario.value.name }}
        </h1>
        <p class="text-[color:var(--color-text-muted)]">
          {{ t('forecast.horizonCaption', { count: horizonMonths }) }}
        </p>
      </div>

      <Card class="flex flex-col gap-[var(--space-cell)]">
        <div class="flex flex-wrap items-center justify-between gap-[var(--space-cell)]">
          <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
            {{ t('forecast.projectionTitle') }}
          </h2>
          <label
            class="flex items-center gap-2 text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
          >
            <span class="text-[color:var(--color-text-muted)]">{{ t('forecast.horizonLabel') }}</span>
            <Select v-model="horizonMonths" :options="horizonOptions" />
          </label>
        </div>

        <p v-if="scenario.isProjecting.value" role="status" class="text-[color:var(--color-text-muted)]">
          {{ t('forecast.loading') }}
        </p>
        <ErrorState
          v-else-if="scenario.projectionError.value"
          :title="t('forecast.projectionErrorTitle')"
          :body="t('forecast.errorBody')"
          :retry-label="t('forecast.retry')"
          :retrying="scenario.projectionRetrying.value"
          @retry="scenario.retry()"
        />
        <ForecastProjectionChart
          v-else-if="scenario.projection.value"
          :points="scenario.projection.value.points"
          :is-flat="isFlat"
        />

        <ForecastTargetCheck
          v-model="targetId"
          :targets="targets.targets.value"
          :evaluation="scenario.targetEvaluation.value"
        />
      </Card>

      <div class="grid grid-cols-1 gap-[var(--space-cell)] lg:grid-cols-2">
        <Card class="flex min-w-0 flex-col gap-[var(--space-cell)]">
          <div class="flex flex-wrap items-center justify-between gap-[var(--space-cell)]">
            <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
              {{ t('forecast.variablesTitle') }}
            </h2>
            <ForecastVariableForm
              v-if="canManage"
              :is-submitting="scenario.isCreatingVariable.value"
              :error-message="variableErrorMessage"
              @create="createVariable"
            />
          </div>
          <ForecastVariableList
            :variables="scenario.variables.value"
            :deleting-id="scenario.deletingVariableId.value"
            @delete="deleteVariable"
          />
        </Card>

        <Card class="flex min-w-0 flex-col gap-[var(--space-cell)]">
          <div class="flex flex-wrap items-center justify-between gap-[var(--space-cell)]">
            <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
              {{ t('forecast.eventsTitle') }}
            </h2>
            <ForecastEventForm
              v-if="canManage"
              :is-submitting="scenario.isCreatingEvent.value"
              :error-message="eventErrorMessage"
              @create="createEvent"
            />
          </div>
          <ForecastEventList
            :events="scenario.events.value"
            :deleting-id="scenario.deletingEventId.value"
            @delete="deleteEvent"
          />
        </Card>
      </div>
    </template>
  </PageShell>
</template>
